import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBookingsSettingsV2BookingsSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/bookings-settings',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/bookings-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_bookings-settings';

/**
 * Retrieves the site's bookings settings.
 *
 * If no settings have been customized for the site yet, the default settings are returned.
 */
export function getBookingsSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __getBookingsSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.v2.bookings_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.settings.v2.BookingsSettingsService.GetBookingsSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsSettingsV2BookingsSettingsServiceUrl({
        protoPath: '/v2/settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'bookingsSettings.createdDate' },
              { path: 'bookingsSettings.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'bookingsSettings.siteProperties.address.coordinates.latitude',
              },
              {
                path: 'bookingsSettings.siteProperties.address.coordinates.longitude',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveWixBookingsSettingsV2BookingsSettingsServiceUrl({
            protoPath: '/v2/settings',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __getBookingsSettings;
}

/**
 * Updates the site's bookings settings.
 *
 * Each time the bookings settings are updated, `revision` increments by 1. The current `revision` must be passed when updating the bookings settings. This ensures you're working with the latest bookings settings and prevents unintended overwrites.
 *
 * This is a partial update: only the fields listed in `fieldMask.paths` are changed. Fields not included in the field mask keep their current values.
 *
 * If no settings have been customized for the site yet, the default settings are created and then updated with the values you provide.
 */
export function updateBookingsSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateBookingsSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'bookingsSettings.createdDate' },
          { path: 'bookingsSettings.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'bookingsSettings.siteProperties.address.coordinates.latitude',
          },
          {
            path: 'bookingsSettings.siteProperties.address.coordinates.longitude',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.v2.bookings_settings',
      method: 'PATCH' as any,
      methodFqn:
        'wix.bookings.settings.v2.BookingsSettingsService.UpdateBookingsSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsSettingsV2BookingsSettingsServiceUrl({
        protoPath: '/v2/bookings-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'bookingsSettings.createdDate' },
              { path: 'bookingsSettings.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'bookingsSettings.siteProperties.address.coordinates.latitude',
              },
              {
                path: 'bookingsSettings.siteProperties.address.coordinates.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBookingsSettings;
}
